// Story 11.8 — the three writes Screen 21's action toolbar hosts (AC8, AC9).
//
// INVALIDATE-AND-REFETCH, ALWAYS. The 200 body of /disable-access and /reactivate
// carries `is_active`, which is a two-valued flag and NOT the four-valued
// access_state the badges render — patching a badge from it would show "active"
// for a reactivated user whose seat is still removed. So no response is written
// into the cache; both keys are invalidated and the server restates the read model
// (inherited finding, 11-4 review).

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import { ADMIN_CONSOLE_QUERY_KEY } from "@/hooks/queries/useAdminConsole";
import { ADMIN_USERS_QUERY_KEY } from "@/hooks/queries/useAdminUsers";
import { adminUserQueryKey } from "@/hooks/queries/useAdminUserDetail";
import type {
  AdminAccessWriteData,
  AdminCalibrationDeadlineWriteData,
  AdminCalibrationDeadlineWriteRequest,
  AdminDisableAccessRequest,
  AdminEnablePaidAccountData,
  AdminEraseUserData,
  AdminResetPasswordData,
} from "@/hooks/queries/admin-users-types";

function adminUserActionPath(userId: string, action: string): string {
  return `/admin/users/${encodeURIComponent(userId)}/${action}`;
}

/**
 * Both keys, every write: the detail screen the action fired from, and the list
 * PREFIX behind it (a partial key match, so every cached filter permutation is
 * refetched rather than only the one the admin last used).
 */
function useInvalidateAdminUser(userId: string) {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: adminUserQueryKey(userId) });
    void queryClient.invalidateQueries({ queryKey: [ADMIN_USERS_QUERY_KEY] });
  };
}

export function useResetAdminUserPassword(userId: string) {
  const invalidate = useInvalidateAdminUser(userId);

  return useMutation({
    // No body by contract — the target is the path, and the temporary password is
    // minted server-side and emailed. It never reaches this client.
    mutationFn: () =>
      apiFetch<AdminResetPasswordData>(adminUserActionPath(userId, "reset-password"), {
        method: "POST",
      }),
    onSuccess: invalidate,
  });
}

export function useDisableAdminUserAccess(userId: string) {
  const invalidate = useInvalidateAdminUser(userId);

  return useMutation({
    // The body is optional; an omitted/blank reason sends `{}` rather than a
    // `reason: ""` the server would have to treat as a supplied empty string.
    mutationFn: (body: AdminDisableAccessRequest = {}) =>
      apiFetch<AdminAccessWriteData>(adminUserActionPath(userId, "disable-access"), {
        method: "POST",
        body,
      }),
    onSuccess: invalidate,
  });
}

export function useReactivateAdminUserAccess(userId: string) {
  const invalidate = useInvalidateAdminUser(userId);

  return useMutation({
    // No body by contract.
    mutationFn: () =>
      apiFetch<AdminAccessWriteData>(adminUserActionPath(userId, "reactivate"), {
        method: "POST",
      }),
    onSuccess: invalidate,
  });
}

/**
 * Story 20.3 — the two FR86 calibration-deadline writes.
 *
 * TWO hooks, not one with a `mode` argument, and the duplication is the point:
 * what distinguishes an extension from a reactivation is the ROUTE and the audit
 * event name it produces (D3). A single hook taking the action as a parameter
 * would make the call site's variable the thing that decides which event lands on
 * a seven-year append-only row — and a variable is exactly what a wrong render
 * can get wrong silently.
 *
 * Same two-key invalidation as every sibling above, and never a cache patch from
 * the 200 body: `calibration_deadline_at` comes back agreeing with the derivation,
 * but `deadline_state` does not come back at all, and it is the field the card's badge renders.
 * Writing the date into the cache without the state is how a reactivated
 * clinician keeps an "On hold" badge over a future deadline.
 *
 * The console key is not invalidated here — the same hole DW-432 already tracks
 * for disable and reactivate, not widened by two more sites. The console's own
 * timeline does gain these events (AC15), so this is a real staleness window of
 * up to `staleTime: 30_000`, recorded rather than silently accepted.
 *
 * NOTHING HERE REACHES THE CLINICIAN, and no amount of key-widening would
 * (Epic 20 close, review finding L5-1). These two writes run in the ADMIN's
 * browser; the clinician reads `calibrationStatusQueryKey()` in a different
 * session, which this cache cannot see. Story 20.2's hold copy promises the
 * restore is immediate, so the release channel is on the clinician's own side:
 * `useCalibrationStatus` polls while `deadline_state === "held"`
 * (`CALIBRATION_HOLD_RECHECK_INTERVAL_MS`). Adding the clinician's key to the
 * list below would look like a fix and change nothing.
 */
export function useExtendCalibrationDeadline(userId: string) {
  const invalidate = useInvalidateAdminUser(userId);

  return useMutation({
    mutationFn: (body: AdminCalibrationDeadlineWriteRequest) =>
      apiFetch<AdminCalibrationDeadlineWriteData>(
        adminUserActionPath(userId, "extend-calibration-deadline"),
        { method: "POST", body },
      ),
    onSuccess: invalidate,
  });
}

export function useReactivateCalibration(userId: string) {
  const invalidate = useInvalidateAdminUser(userId);

  return useMutation({
    mutationFn: (body: AdminCalibrationDeadlineWriteRequest) =>
      apiFetch<AdminCalibrationDeadlineWriteData>(
        adminUserActionPath(userId, "reactivate-calibration"),
        { method: "POST", body },
      ),
    onSuccess: invalidate,
  });
}

/**
 * Story 16.7 (AC15) — the paid-account rescue: FR39's last arrow, opened by hand
 * when the event that normally opens it did not arrive.
 *
 * Same two-key invalidation as its siblings, and for the sharper version of the
 * same reason: this write moves `access_state` from `invited` to `active` AND
 * settles the calibration gate behind it, neither of which is readable off the
 * `is_active` flag in the 200 body. The console restates the row.
 *
 * The console key is deliberately NOT invalidated here — the same hole DW-432
 * already tracks for disable and reactivate, not widened by a third site.
 */
export function useEnablePaidAccount(userId: string) {
  const invalidate = useInvalidateAdminUser(userId);

  return useMutation({
    // No body by contract — the target is the path, and the action takes no
    // options. The money precondition is read server-side; there is nothing for
    // this client to assert about it.
    mutationFn: () =>
      apiFetch<AdminEnablePaidAccountData>(
        adminUserActionPath(userId, "enable-paid-account"),
        { method: "POST" },
      ),
    onSuccess: invalidate,
  });
}

/**
 * Story 11.12 (AC10, FR56d) — the erasure. No body by contract; the target is the
 * path and the action takes no options.
 *
 * Invalidates the same two keys as its three siblings — plus the console, see
 * below — even though the caller navigates away on success. The detail key
 * matters precisely because the row
 * survives erasure: the account is not deleted, it is emptied, so a cached detail
 * page still holding the pre-erasure name and address must not be served if the
 * admin navigates back to it. Dropping the detail invalidation here because "we
 * are leaving anyway" is how a tombstoned account keeps rendering its old
 * identity out of the cache.
 */
export function useEraseAdminUser(userId: string) {
  const invalidate = useInvalidateAdminUser(userId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiFetch<AdminEraseUserData>(adminUserActionPath(userId, "erase"), {
        method: "POST",
      }),
    onSuccess: () => {
      invalidate();
      // A THIRD key, on this write only (Story 15.7). Erasure now subtracts the
      // account from BOTH clinician tiles on the operational console — the
      // console query drops erased ids before it resolves entitlements, so
      // "Registered clinicians" and "Active clinicians" both move. Without this
      // the console serves a `staleTime: 30_000` snapshot (App.tsx) counting a
      // person the platform has just erased, which is the one number nobody
      // wants to see stale.
      //
      // Only erasure, deliberately. Disable and reactivate move `blocked_users`
      // and `live_scoring_enabled` the same way and are NOT invalidated here;
      // that hole predates this story and is tracked rather than widened into
      // it (DW-432).
      void queryClient.invalidateQueries({ queryKey: [ADMIN_CONSOLE_QUERY_KEY] });
    },
  });
}
